import * as React from 'react';
import { ChevronRight, X } from 'lucide-react';
import { cn } from '../lib/utils';

export interface WorkflowHintProps extends React.ComponentPropsWithoutRef<'div'> {
  hint: string;
  defaultExpanded?: boolean;
  dismissKey?: string;
}

function isDismissed(key: string): boolean {
  try {
    return localStorage.getItem(`coliseum-hint-${key}`) === 'dismissed';
  } catch {
    return false;
  }
}

function setDismissed(key: string): void {
  try {
    localStorage.setItem(`coliseum-hint-${key}`, 'dismissed');
  } catch {
    // SSR or localStorage unavailable
  }
}

export function WorkflowHint({
  hint,
  defaultExpanded = false,
  dismissKey,
  className,
  children,
  ...props
}: WorkflowHintProps) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const [dismissed, setDismissedState] = React.useState(false);

  React.useEffect(() => {
    if (dismissKey && isDismissed(dismissKey)) {
      setDismissedState(true);
    }
  }, [dismissKey]);

  if (dismissed) return null;

  function handleDismiss(e: React.MouseEvent) {
    e.stopPropagation();
    if (dismissKey) {
      setDismissed(dismissKey);
      setDismissedState(true);
    }
  }

  return (
    <div
      className={cn('rounded-lg bg-subtle px-4 py-3', className)}
      {...props}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-expanded={children ? expanded : undefined}
          onClick={() => setExpanded((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-sm text-left"
        >
          <ChevronRight
            aria-hidden="true"
            className={cn(
              'size-4 shrink-0 text-muted-foreground transition-transform',
              expanded && 'rotate-90'
            )}
          />
          <span className="text-sm text-muted-foreground">{hint}</span>
        </button>
        {dismissKey ? (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss hint"
            className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {expanded && children ? (
        <div className="mt-3 text-sm text-muted-foreground">{children}</div>
      ) : null}
    </div>
  );
}
